import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { getUseCaseBySlug } from "../data/useCases";
import { arrowL, arrowML, arrowMR, arrowR } from "../assets/images/image";

const slugToTKey: Record<string, string> = {
  "sales-team": "salesTeam",
  "customer-support": "customerSupport",
  "content-creator-media": "contentCreatorMedia",
  "hr-recruitment": "hrRecruitment",
  "team-meeting-task-automation": "teamMeetingTaskAutomation",
  "marketing-project": "marketingProject",
};

const UseCaseDetail: React.FC = () => {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const useCase = slug ? getUseCaseBySlug(slug) : undefined;
  const [showcaseMode, setShowcaseMode] = useState<"photo" | "video">("photo");
  const [carouselIndex, setCarouselIndex] = useState(0);
  const slugKey = slug && slugToTKey[slug] ? slugToTKey[slug] : "";
  const headline = slugKey ? t(`useCaseDetail.${slugKey}.headline`) : useCase?.headline ?? "";
  const description = slugKey ? t(`useCaseDetail.${slugKey}.description`) : useCase?.description ?? "";
  const cleanDescription = description.replace(/<\/?strong>/g, "");
  const insightTitle = slugKey
    ? t(`useCaseDetail.${slugKey}.insightTitle`, { defaultValue: headline })
    : headline;
  const insightBody = slugKey
    ? t(`useCaseDetail.${slugKey}.insightBody`, { defaultValue: cleanDescription })
    : cleanDescription;

  if (!useCase) {
    return <Navigate to="/use-cases" replace />;
  }

  const captions = useCase.showcaseCaptions ?? [
    "Key Highlights",
    "How the Workflow Runs",
    "Impact & Next Steps",
  ];
  const currentCaption = captions[carouselIndex] ?? captions[0];

  const goPrev = () => {
    setCarouselIndex((i) => (i <= 0 ? captions.length - 1 : i - 1));
  };
  const goNext = () => {
    setCarouselIndex((i) => (i >= captions.length - 1 ? 0 : i + 1));
  };

  return (
    <div className="px-4 md:px-0 pt-6 md:pt-8 pb-8 max-w-full">
      {/* Breadcrumb */}
      <nav className="text-[#494949] text-xs md:text-sm mb-4 md:mb-6" aria-label="Breadcrumb">
        <Link to="/use-cases" className="df-color hover:underline">
          {t("useCases.breadcrumb")}
        </Link>
        <span className="mx-1">&gt;</span>
        <span className="text-[#494949]">{slugKey ? t(`useCases.${slugKey}.title`) : useCase.title}</span>
      </nav>

      {/* Page title — căn giữa */}
      <h1 className="text-[#494949] text-3xl md:text-5xl leading-tight mb-6 md:mb-10 text-center font-light">
        {slugKey ? t(`useCases.${slugKey}.title`) : useCase.title}
      </h1>

      {/* Mobile: ảnh full width → headline → mô tả. Desktop: 2 cột */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 lg:gap-12 mb-10 md:mb-14">
        <div className="lg:col-span-5 w-full">
          <div
            className="rounded-xl overflow-hidden aspect-[16/10] md:aspect-[780/384] bg-[#e5e5e5] bg-cover bg-center w-full"
            style={{
              backgroundImage: useCase.image
                ? `url("${useCase.image}")`
                : undefined,
            }}
          />
        </div>
        <div className="lg:col-span-7 flex flex-col justify-start">
          <h2 className="text-[#494949] text-xl md:text-2xl font-light font-[274] mb-3 md:mb-4 text-center md:text-left">
            {headline}
          </h2>
          <p
            className="text-[#494949] text-sm md:text-base leading-6 text-center md:text-left"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </div>
      </div>

      {/* Showcase: pills → media → carousel (mobile: mũi tên + 1/4 dưới media) */}
      <div className="flex flex-col w-full">
        <div className="flex justify-center mb-4 md:mb-6 w-full">
          <div className="flex rounded-full bg-[#E8E8E8] p-1 w-full max-w-[320px] md:w-fit inner-shadow">
            <button
              type="button"
              onClick={() => setShowcaseMode("photo")}
              className={`flex-1 md:flex-none px-4 md:px-6 py-2.5 rounded-full text-xs md:text-sm font-medium transition-colors ${
                showcaseMode === "photo"
                  ? "bg-[#3A57FF] text-white"
                  : "text-[#494949]"
              }`}
            >
              {t("useCaseDetail.photoShowcase")}
            </button>
            <button
              type="button"
              onClick={() => setShowcaseMode("video")}
              className={`flex-1 md:flex-none px-4 md:px-6 py-2.5 rounded-full text-xs md:text-sm font-medium transition-colors ${
                showcaseMode === "video"
                  ? "bg-[#3A57FF] text-white"
                  : "text-[#494949]"
              }`}
            >
              {t("useCaseDetail.videoShowcase")}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 w-full">
          <div className="lg:col-span-8 flex flex-col items-start w-full order-1">
            {/* Desktop: nav + caption trên media */}
            <div className="hidden lg:flex items-center gap-2 mb-3 w-full">
              <button
                type="button"
                onClick={goPrev}
                className="flex w-12 h-12 items-center justify-center rounded-lg bg-[#3A57FF] text-white flex-shrink-0"
                aria-label="Previous"
              >
                <img src={arrowL} alt="" className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={goNext}
                className="flex w-12 h-12 items-center justify-center rounded-lg bg-[#3A57FF] text-white flex-shrink-0"
                aria-label="Next"
              >
                <img src={arrowR} alt="" className="w-5 h-5" />
              </button>
              <div className="flex-1 min-w-0 h-12 px-4 flex items-center rounded-lg bg-[#3A57FF] text-white text-sm font-medium text-left">
                {currentCaption}
              </div>
            </div>

            {/* Media placeholder */}
            <div
              className="w-full overflow-hidden flex items-center justify-center rounded-lg bg-[#E0E0E0] border-0 md:border-2 md:border-[#007bff]"
              style={{ aspectRatio: "780/384" }}
            >
              <AnimatePresence mode="wait">
                {showcaseMode === "photo" ? (
                  <motion.div
                    key="photo"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="w-full h-full bg-cover bg-center min-h-[200px] md:min-h-[384px]"
                    style={{
                      backgroundImage: useCase.image
                        ? `url("${useCase.image}")`
                        : undefined,
                    }}
                  />
                ) : (
                  <motion.div
                    key="video"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="w-full h-full relative flex items-center justify-center min-h-[200px] md:min-h-[384px]"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-[#2A2A2A] to-[#111111]" />
                    <div className="relative z-10 text-white text-xl md:text-3xl font-semibold tracking-wide uppercase">
                      Coming Soon
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Mobile: carousel controls (mũi tên + 1/4) ngay dưới media */}
            <div className="lg:hidden flex items-center justify-between gap-4 py-4 w-full">
                <button
                            type="button"
                            onClick={goPrev}
                            className="flex w-8 h-8 items-center justify-center rounded-full bg-[#D9D9D9] text-[#494949] flex-shrink-0"
                            aria-label="Previous"
                        >
                            <img src={arrowML} alt="" className="w-[18px] h-[18px]" />
                        </button>
                        <span className="text-[#494949] text-[14px] font-medium">
                            <span className="font-bold">{carouselIndex + 1}</span>/{captions.length}
                        </span>
                        <button
                            type="button"
                            onClick={goNext}
                            className="flex w-8 h-8 items-center justify-center rounded-full bg-[#D9D9D9] text-[#494949] flex-shrink-0"
                            aria-label="Next"
                        >
                            <img src={arrowMR} alt="" className="w-[18px] h-[18px]" />
                        </button>
            </div>

            {/* Pagination dots — desktop; mobile tùy chọn giữ hoặc ẩn */}
            <div className="hidden md:flex items-center gap-2 mt-4">
              {captions.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setCarouselIndex(i)}
                  className={`h-1 rounded-sm transition-colors ${
                    carouselIndex === i ? "bg-[#3A57FF] w-8" : "bg-[#E0E0E0] w-6"
                  }`}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>
          </div>

          {/* col-4: detail content by use case */}
          <div className="lg:col-span-4 pt-0 lg:pt-[60px] text-left order-2">
            <p className="text-[#494949] text-xl md:text-2xl font-light font-[274] mb-3 md:mb-4 text-center md:text-left">
              {insightTitle}
            </p>
            <p className="text-[#494949] text-sm md:text-base leading-6 text-center md:text-left">
              {insightBody}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UseCaseDetail;
